/**
 * learningFunnel.js — ONE measurement model for learning (not page views). Controlled event names, no child PII.
 * Sends to GA4 via the existing `track()` AND batches to /api/funnel/events so the funnel
 * DISCOVER → START → MEANINGFUL ACTIVITY → COMPLETE → EVIDENCE → MASTERY → NEXT → RETURN is measurable server-side.
 */
import { track } from "@/lib/track";

export const FUNNEL_EVENTS = new Set(["homepage_view", "today_started", "today_completed", "subject_selected", "objective_selected", "learning_started", "lesson_started", "lesson_completed",
  "activity_started", "activity_completed", "practice_started", "practice_completed", "assessment_started", "assessment_completed", "answer_submitted", "feedback_received", "evidence_generated",
  "mastery_secure", "mastery_partial", "mastery_struggling", "next_objective_started", "game_started", "game_completed", "reading_started", "reading_completed", "creative_started", "creative_completed",
  "live_started", "live_completed", "return_to_learning"]);
const STAGE = { homepage_view: "DISCOVER", subject_selected: "DISCOVER", objective_selected: "DISCOVER", today_started: "START", learning_started: "START", lesson_started: "START", activity_started: "START", practice_started: "START", assessment_started: "START", game_started: "START", reading_started: "START", creative_started: "START", live_started: "START",
  answer_submitted: "MEANINGFUL", feedback_received: "MEANINGFUL", lesson_completed: "COMPLETE", activity_completed: "COMPLETE", practice_completed: "COMPLETE", assessment_completed: "COMPLETE", game_completed: "COMPLETE", reading_completed: "COMPLETE", creative_completed: "COMPLETE", live_completed: "COMPLETE", today_completed: "COMPLETE",
  evidence_generated: "EVIDENCE", mastery_secure: "MASTERY", mastery_partial: "MASTERY", mastery_struggling: "MASTERY", next_objective_started: "NEXT", return_to_learning: "RETURN" };
const ALLOWED = ["subject", "objective", "activityType", "activityId", "entry", "year", "ageBand", "score", "total", "durationSec", "completion", "setBy", "route", "reason"];
const SESSION_KEY = "mwq_funnel_session_v1";
const API = process.env.REACT_APP_BACKEND_URL;
let queue = [];
let timer = null;

function session() {
  try {
    const s = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (s && Date.now() - s.last < 30 * 60 * 1000) { s.last = Date.now(); sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); return s; }
    const first = !localStorage.getItem("mwq_funnel_seen");
    localStorage.setItem("mwq_funnel_seen", "1");
    const n = { id: Math.random().toString(36).slice(2, 12), start: Date.now(), last: Date.now(), returning: !first };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(n));
    if (n.returning) queue.push({ event: "return_to_learning", stage: "RETURN", props: {}, sessionId: n.id, at: Date.now() });
    return n;
  } catch { return { id: "na", start: Date.now(), returning: false }; }
}

const ageBand = () => { try { return { little: "seedlings", big: "explorers", older: "pioneers" }[localStorage.getItem("we_age_group")] || null; } catch { return null; } };

function flush() {
  timer = null;
  if (!queue.length || typeof navigator !== "undefined" && navigator.onLine === false) return;
  try { if (sessionStorage.getItem("mwq_airline_session") === "1" || localStorage.getItem("mwq_runtime_profile") === "AIRLINE_EVALUATION") { queue.length = 0; return; } } catch { /* ignore */ } // shared sessions: nothing leaves the device
  const batch = queue.splice(0, 50);
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) navigator.sendBeacon(`${API}/api/funnel/events`, new Blob([body], { type: "application/json" }));
    else fetch(`${API}/api/funnel/events`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch { /* ignore */ }
}

/** funnel("lesson_completed", { subject, objective, ... }) — unknown event names and non-allowlisted props are dropped (privacy). */
export function funnel(event, props = {}) {
  if (!FUNNEL_EVENTS.has(event)) return;
  const s = session();
  const clean = Object.fromEntries(Object.entries(props).filter(([k, v]) => ALLOWED.includes(k) && v != null && String(v).length <= 80));
  clean.ageBand = clean.ageBand || ageBand();
  clean.sessionSec = Math.round((Date.now() - s.start) / 1000);
  track(event, clean);
  queue.push({ event, stage: STAGE[event] || "OTHER", props: clean, sessionId: s.id, at: Date.now() });
  if (!timer) timer = setTimeout(flush, 4000);
}
if (typeof window !== "undefined") window.addEventListener("pagehide", flush);
